import * as React from 'react';
import { useTranslation } from "react-i18next";

import { AutoComplete } from '@progress/kendo-react-dropdowns';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import CustomCommandCellAnimalMovements  from '../ButtonComponentsPanel/CustomCommandCellAnimalMovements';

import { ProcessError, ErrorCode } from '../../classes/ExceptionProcessor';
import { ListOfPaged, MinimumPage } from '../../classes/consts';

import FarmsService from "../../services/farms.service";

export default function FarmMovementsComponents(props) {
    const { t } = useTranslation();
    // Error Hooks
    const [visibleError, setVisibleError] = React.useState(false);
    const [errorData, setErrorData] = React.useState({ messageError: "", code: "" });

    // Grid Hooks
    const [orderByString, setOrderByString] = React.useState("?order=description asc&filter=isClosed eq false");
    const [sortState, setSortState] = React.useState([]);
    const [pageState, setPageState] = React.useState({ skip: 0, take: MinimumPage() });

    // Loading Hooks
    const [loading, setLoading] = React.useState(true);
    const closeError = React.useCallback(() => { setVisibleError(false) }, []);

    const [data, setData] = React.useState([]);

    //#region DataFilters
    //#region Movements Description Filter
    const [movementDescriptionFilter, setmovementDescriptionFilter] = React.useState("");

    const handlemovementDescriptionFilterChange = (event) => {
        const value = event.value || "";
        setmovementDescriptionFilter(value);
    };
    //#endregion

    //#region Movements Type Filter
    const [movementTypeDescriptionFilter, setmovementTypeDescriptionFilter] = React.useState("");

    const handlemovementTypeDescriptionFilterChange = (event) => {
        const value = event.value || "";
        setmovementTypeDescriptionFilter(value);
    };
    //#endregion

    //#region IsClosed Filter
    const isClosedOptions = [
        { key: "open", label: t("FarmMovements_Status_Open") },
        { key: "closed", label: t("FarmMovements_Status_Closed") },
        { key: "all", label: t("FarmMovements_Status_All") }
    ];

    const [isClosedFilter, setIsClosedFilter] = React.useState("open");

    const handleIsClosedFilterChange = React.useCallback((e) => {
        let selected;
        if (e?.value !== undefined) selected = e.value;
        else if (e?.target && e.target.value !== undefined) selected = e.target.value;
        else if (e?.target && e.target.dataItem !== undefined) selected = e.target.dataItem;

        const key = selected?.key ?? selected ?? "all";
        setIsClosedFilter(key);
    }, []);
    //#endregion
    
    //#region Sorting Filter
    const applySortingAndFiltering = React.useCallback((sortOverride) => {
        const sort = sortOverride || sortState;

        const sortField = sort?.[0]?.field || "description";
        const sortDir = sort?.[0]?.dir || "asc";
        const order = `${sortField} ${sortDir}`;

        const filterParts = [];

        if (movementDescriptionFilter) filterParts.push(`contains(description,'${movementDescriptionFilter}')`);
        if (movementTypeDescriptionFilter) filterParts.push(`contains(movementTypeDescription,'${movementTypeDescriptionFilter}')`);

        if (isClosedFilter === "closed") filterParts.push("isClosed eq true");
        else if (isClosedFilter === "open") filterParts.push("isClosed eq false");

        const filter = filterParts.join(" and ");
        const queryString = `?order=${encodeURIComponent(order)}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;

        setOrderByString(queryString);
    }, [sortState, movementDescriptionFilter, movementTypeDescriptionFilter, isClosedFilter]);
    //#endregion
    //#endregion

    React.useEffect(() => 
    {
        if (props.farm.farmId !== 0)
        {
            setLoading(true);

            FarmsService.getFilteredFarmMovements(props.farm.farmId, orderByString)
                .then(response => response.data)
                .then(json => {
                    setData(json.contract || []);
                    setLoading(false);
                }).catch(err => {
                    setErrorData({ messageError: ProcessError(err), code: ErrorCode(err) });
                    setVisibleError(true);
                    setLoading(false);
                });
        }
    }, [props.farm, orderByString]);

    const CommandCell = React.useCallback(cellProps => (
        <CustomCommandCellAnimalMovements
            {...cellProps}
            linkToDetails={'/Farms/' + cellProps.dataItem.farmId + '/Movements/' + cellProps.dataItem.movementId + '/Details'}
            isAdmin={props.isAdmin}
        />
    ), [props]);

    const loadingPanel =
        <div className="k-loading-mask">
            <span className="k-loading-text">Loading</span>
            <div className="k-loading-image"></div>
            <div className="k-loading-color"></div>
        </div>;

    const loadingCell = React.useCallback((tdElement, cellProps) => {
        if (cellProps.dataItem[cellProps.field] === undefined) {
            // shows loading cell if no data
            return <td> <span className="k-placeholder-line"></span></td>;
        }

        // default rendering for this cell
        return tdElement;
    }, []);

    const sortChange = React.useCallback((event) => {
        setSortState(event.sort);
        applySortingAndFiltering(event.sort);
    });

    const pageChange = React.useCallback((event) => {
        setPageState({ ...pageState, skip: event.page.skip, take: event.page.take });
    }, [pageState]);

    const StatusCell = (props) => {
        const { dataItem } = props;
        const isActive = !dataItem.isClosed;

        return (
            <td style={{ textAlign: "center" }}>
                <span className={`k-icon ${isActive ? "k-i-check" : "k-i-close"}`} />
            </td>
        );
    };

    const processedData = (data.length > 0 || movementDescriptionFilter
        ? data
        : data).slice(pageState.skip, pageState.skip + pageState.take);

    return (
        <div>
            {visibleError && (
                <Dialog title={"Error " + errorData.code} onClose={closeError}>
                    <p style={{ margin: "25px", textAlign: "center" }}>
                        <b>{errorData.messageError}</b>
                    </p>
                    <DialogActionsBar>
                        <button className="k-button" onClick={() => { setVisibleError(false) }}>
                            OK
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {loading && loadingPanel}

            <div className="container-fluid">
                <div className="card">
                    <div className="card-header m-0 p-3">
                        <div className="row g-3 w-100 align-items-end">

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                    <FloatingLabel label={t("FarmMovements_Status")} editorId="isClosedInput">
                                        <DropDownList
                                            id="isClosedInput"
                                            loading={loading}
                                            style={{ width: '100%' }}
                                            data={isClosedOptions}
                                            textField="label"
                                            dataItemKey="key"
                                            value={isClosedOptions.find(opt => opt.key === isClosedFilter)}
                                            onChange={handleIsClosedFilterChange}
                                        />
                                    </FloatingLabel>
                                </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("FarmGroups_GroupDescription")} editorId="descriptionInput">
                                    <AutoComplete
                                        id="descriptionInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(feedReceipt => feedReceipt.description))]}
                                        value={movementDescriptionFilter}
                                        onChange={handlemovementDescriptionFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("FarmMovements_MovementType")} editorId="movementTypeInput">
                                    <AutoComplete
                                        id="movementTypeInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(m => m.movementTypeDescription))]}
                                        value={movementTypeDescriptionFilter}
                                        onChange={handlemovementTypeDescriptionFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <button
                                    className="k-button k-primary w-100"
                                    style={{ marginTop: '25px' }}
                                    onClick={() => applySortingAndFiltering()}
                                    title={t("Farms_Button_ApplySorting")}
                                >
                                    {t("Farms_Button_ApplySorting")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <br />

                <div className="table-responsive">
                    <Grid
                        cellRender={loadingCell}
                        pageable={{ pageSizes: ListOfPaged() }}
                        data={processedData}
                        skip={pageState.skip}
                        take={pageState.take}
                        total={
                            (data.length > 0 ||
                                movementDescriptionFilter
                                ? data.length
                                : data.length)
                        }
                        scrollable={'none'}
                        sortable={{ allowUnsort: true, mode: "single" }}
                        sort={sortState}
                        onSortChange={sortChange}
                        onPageChange={pageChange}
                    >

                        <Column cell={CommandCell} width={"130px"} />
                        <Column title={t("FarmMovements_Status")} cell={StatusCell} width="65px" sortable={false} />
                        <Column field="movementDate" title={t("FarmMovements_MovementDate")} sortable={true} />
                        <Column field="movementTypeDescription" title={t("FarmMovements_MovementType")} sortable={true} />
                        <Column field="description" title={t("FarmMovements_Description")} sortable={true} />
                        <Column field="heads" title={t("FarmMovements_Heads")} sortable={true} />
                    </Grid>
                </div>
            </div>
        </div>
    );
}